import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  TurnReportResource,
  TurnReportsGetData,
  TurnReportsGetResponse,
  TurnResource,
} from '~/generated';
import { client } from '~/generated/client.gen';

type TurnReport = Omit<TurnReportResource, 'turn'> & {
  turn: Omit<TurnResource, 'id'> & {
    id: number;
  };
};

export const turnReportsGet = async (
  data: Omit<TurnReportsGetData, 'url'> = {},
  responseHeader?: string
): Promise<TurnReportsGetResponse> => {
  // Use the client directly to get access to response headers
  const response = await client.get({
    url: '/TurnReports',
    query: data.query,
    responseType: 'json', // Use json instead of blob to get parsed data
  });

  // Check if response is an error
  if ('error' in response && response.error !== undefined) {
    throw response;
  }

  // Type guard to ensure we have a successful response
  if (!('headers' in response) || !('data' in response)) {
    throw new Error('Unexpected response type');
  }

  // If we need a specific header, return it
  if (responseHeader) {
    const headerValue = response.headers[responseHeader.toLowerCase()];
    if (headerValue) {
      return headerValue as unknown as TurnReportsGetResponse;
    }
  }

  // Return the data from the response
  // The response.data contains the actual response body
  return response.data as TurnReportsGetResponse;
};

type TurnReportsResult = {
  turnReports: TurnReport[];
  totalCount: number;
};

export const useTurnReports = (
  options: Omit<TurnReportsGetData, 'url'> = {},
  queryOptions?: Omit<
    UseQueryOptions<TurnReportsResult, unknown, TurnReportsResult, unknown[]>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryFn: async () => {
      const totalCount = (await turnReportsGet(
        options,
        'total-count'
      )) as unknown as string;
      const response = (await turnReportsGet(
        options
      )) as unknown as TurnReport[];
      return {
        turnReports: response,
        totalCount: parseInt(totalCount, 10),
      };
    },
    queryKey: ['TurnReports', options],
    ...queryOptions,
  });
